import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVerticalIcon, PlusIcon, SearchIcon, XIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  DEFAULT_ACCOUNT_COLUMNS,
  DEFAULT_CONTACT_COLUMNS
} from './defaultColumns';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// GraphQL introspection query to get actual schema fields
const GET_SCHEMA_FIELDS = gql`
  query GetSchemaFields {
    __type(name: "AccountNode") {
      name
      fields {
        name
        type {
          name
          kind
          ofType {
            name
            kind
          }
        }
      }
    }
    contactType: __type(name: "ContactNode") {
      name
      fields {
        name
        type {
          name
          kind
          ofType {
            name
            kind
          }
        }
      }
    }
  }
`;

export interface ColumnConfig {
  field: string;
  isRelated?: boolean;
  label: string;
  relationLabel?: string;
  type: string;
}

interface ColumnSelectorProps {
  onChange: (columns: ColumnConfig[]) => void;
  selectedColumns: ColumnConfig[];
  targetModel: 'account' | 'contact';
}

interface SchemaField {
  name: string;
  type: {
    kind: string;
    name?: string;
    ofType?: {
      kind: string;
      name?: string;
    };
  };
}

interface SortableColumnItemProps {
  column: ColumnConfig;
  index: number;
  onRemove: (field: string) => void;
}

export function ColumnSelector({
  onChange,
  selectedColumns,
  targetModel
}: ColumnSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const { data, loading } = useQuery(GET_SCHEMA_FIELDS, {
    fetchPolicy: 'cache-first'
  });

  // Extract available fields from GraphQL schema
  const availableFields = useMemo(() => {
    if (!data) return [];

    const typeData = targetModel === 'account' ? data.__type : data.contactType;
    if (!typeData?.fields) return [];

    // Filter out internal fields and unsuitable types
    const fields = typeData.fields
      .filter((field: SchemaField) => {
        // Skip internal/system fields
        if (field.name.startsWith('_')) return false;

        // Skip cursor (used for pagination)
        if (field.name === 'cursor') return false;

        // Skip complex nested types that aren't suitable for table columns
        const typeName = field.type.name || field.type.ofType?.name;
        const typeKind = field.type.kind || field.type.ofType?.kind;

        // Skip list types (one2many, many2many)
        if (field.type.kind === 'LIST' || field.type.ofType?.kind === 'LIST') {
          return false;
        }

        // Skip OBJECT types (relations like UserNode, AccountNode, etc.)
        // These require subfield selection which our query builder doesn't handle
        if (typeKind === 'OBJECT' && typeName?.endsWith('Node')) {
          return false;
        }

        return true;
      })
      .map((field: SchemaField) => {
        const fieldType = mapGraphQLTypeToColumnType(field);
        return {
          field: field.name,
          isRelated: false,
          label: fieldNameToLabel(field.name),
          type: fieldType
        };
      });

    return fields;
  }, [data, targetModel]);

  // Filter fields based on search term
  const filteredFields = useMemo(() => {
    if (!searchTerm) return availableFields;
    const search = searchTerm.toLowerCase();
    return availableFields.filter(
      (f: ColumnConfig) =>
        f.label.toLowerCase().includes(search) ||
        f.field.toLowerCase().includes(search)
    );
  }, [availableFields, searchTerm]);

  // Set of selected field names for quick lookup
  const selectedFieldSet = useMemo(
    () => new Set(selectedColumns.map(col => col.field)),
    [selectedColumns]
  );

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = selectedColumns.findIndex(
        col => col.field === active.id
      );
      const newIndex = selectedColumns.findIndex(col => col.field === over.id);

      onChange(arrayMove(selectedColumns, oldIndex, newIndex));
    }
  };

  const handleAddColumn = (field: ColumnConfig) => {
    onChange([...selectedColumns, field]);
  };

  const handleRemoveColumn = (fieldName: string) => {
    onChange(selectedColumns.filter(col => col.field !== fieldName));
  };

  const handleResetToDefaults = () => {
    // Default columns based on target model
    const defaults =
      targetModel === 'account'
        ? DEFAULT_ACCOUNT_COLUMNS
        : DEFAULT_CONTACT_COLUMNS;
    onChange(defaults);
  };

  if (loading) {
    return (
      <div className="text-sm text-gray-500">Loading available columns...</div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Column Configuration</Label>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">
            {selectedColumns.length} column
            {selectedColumns.length !== 1 ? 's' : ''} selected
          </span>
          <Button
            onClick={handleResetToDefaults}
            size="sm"
            type="button"
            variant="outline"
          >
            Reset to Defaults
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Available Fields */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Available Fields</Label>
          <div className="relative">
            <SearchIcon className="absolute top-2.5 left-2 size-4 text-gray-400" />
            <Input
              className="pl-8"
              onChange={e => {
                setSearchTerm(e.target.value);
              }}
              placeholder="Search fields..."
              type="text"
              value={searchTerm}
            />
          </div>
          <div className="h-[400px] overflow-auto rounded-md border">
            <div className="space-y-1 p-2">
              {filteredFields.length > 0 ? (
                filteredFields.map((field: ColumnConfig) => (
                  <button
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-800"
                    disabled={selectedFieldSet.has(field.field)}
                    key={field.field}
                    onClick={() => {
                      handleAddColumn(field);
                    }}
                    type="button"
                  >
                    <PlusIcon className="size-4 shrink-0" />
                    <span className="truncate">{field.label}</span>
                  </button>
                ))
              ) : (
                <div className="px-2 py-8 text-center text-sm text-gray-500">
                  No fields found
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Selected Columns */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Selected Columns (Drag to Reorder)
          </Label>
          <div className="h-[450px] overflow-auto rounded-md border">
            {selectedColumns.length === 0 ? (
              <div className="flex h-full items-center justify-center p-8 text-center text-sm text-gray-500">
                No columns selected.
                <br />
                Select fields from the left to add columns.
              </div>
            ) : (
              <div className="space-y-2 p-2">
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                  sensors={sensors}
                >
                  <SortableContext
                    items={selectedColumns.map(col => col.field)}
                    strategy={verticalListSortingStrategy}
                  >
                    {selectedColumns.map((col, index) => (
                      <SortableColumnItem
                        column={col}
                        index={index}
                        key={col.field}
                        onRemove={handleRemoveColumn}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Convert field name from camelCase to Title Case for label
function fieldNameToLabel(fieldName: string): string {
  // Handle common patterns
  if (fieldName === 'id') return 'ID';

  // Convert camelCase to space-separated words
  const words = fieldName.replace(/([A-Z])/g, ' $1').trim();

  // Capitalize first letter of each word
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Map GraphQL types to our internal types
function mapGraphQLTypeToColumnType(field: SchemaField): string {
  const typeName = field.type.name || field.type.ofType?.name;

  if (!typeName) return 'char';

  // Map GraphQL scalar types to column types
  const typeMap: Record<string, string> = {
    Boolean: 'boolean',
    Date: 'date',
    DateTime: 'datetime',
    Float: 'float',
    ID: 'char',
    Int: 'integer',
    String: 'char'
  };

  // Check if it's a known type
  if (typeMap[typeName]) {
    return typeMap[typeName];
  }

  // If it ends with Node, it's likely a relation (many2one)
  if (typeName.endsWith('Node')) {
    return 'many2one';
  }

  // Default to char for unknown types
  return 'char';
}

function SortableColumnItem({
  column,
  index,
  onRemove
}: SortableColumnItemProps) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: column.field });

  const style = {
    opacity: isDragging ? 0.5 : 1,
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <div
      className="flex items-center gap-2 rounded-md border bg-white p-2 dark:bg-gray-900"
      ref={setNodeRef}
      style={style}
    >
      <button
        className="cursor-grab touch-none text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
        type="button"
        {...attributes}
        {...listeners}
      >
        <GripVerticalIcon className="size-4" />
      </button>
      <div className="flex size-6 shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
        {index + 1}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{column.label}</div>
        {column.isRelated && column.relationLabel && (
          <div className="truncate text-xs text-gray-500">
            {column.relationLabel}
          </div>
        )}
      </div>
      <Button
        onClick={() => {
          onRemove(column.field);
        }}
        size="icon"
        type="button"
        variant="ghost"
      >
        <XIcon className="size-4" />
      </Button>
    </div>
  );
}
